import { firebaseAuth } from './firebase';
import type {
  StudentRow, LeadRow, AnalyticsOverview, LeadsTrendPoint, StudentStatusSlice,
  UserRow, AppointmentRow, TaskRow, LeaveRow, FollowUpRow, CounselorPerfRow, NotificationRow,
  ApplicationRow, InstitutionRow, CourseRow, DocumentRow, InvoiceRow, ExpenseRow, VisaRow,
  ConsultancyRow, EmployeeRow, AttendanceRow, ActivityRow, LeadConversionRow, AppointmentSummaryRow,
  BulkResult, InvoicePaymentRow, AttendanceToday,
  LeaveTypeRow, HolidayRow, PayrollRunRow, PayslipRow,
} from './types';

export interface FacePunchResult {
  employeeId: number; name: string; employeeCode: string;
  action: 'checked-in' | 'checked-out' | 'already-complete'; confidence: number;
}
export interface LeaveBalances {
  types: { name: string; annualQuota: number; isPaid: number | boolean }[];
  taken: { employeeId: number; leaveType: string | null; days: number | string }[];
}

const API_URL = import.meta.env.VITE_API_URL || '/api';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) { super(message); this.status = status; }
}

export async function apiRequest<T>(path: string, options: Parameters<typeof fetch>[1] = {}): Promise<T> {
  const user = firebaseAuth.currentUser;
  if (!user) throw new ApiError(401, 'Authentication required');
  const token = await user.getIdToken();
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(response.status, payload.error || 'Request failed');
  return payload as T;
}

/** POST multipart/form-data (file uploads). Lets the browser set the boundary header. */
export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const user = firebaseAuth.currentUser;
  if (!user) throw new ApiError(401, 'Authentication required');
  const token = await user.getIdToken();
  const response = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(response.status, payload.error || 'Upload failed');
  return payload as T;
}

/** Fetch a protected file with the auth header and open it in a new browser tab. */
export async function openAuthedFile(path: string): Promise<void> {
  const user = firebaseAuth.currentUser;
  if (!user) throw new ApiError(401, 'Authentication required');
  const token = await user.getIdToken();
  const response = await fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new ApiError(response.status, payload.error || 'Could not open file');
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const api = {
  provisionConsultancy: (data: unknown) => apiRequest<{ data: { provisioned: boolean } }>('/auth/provision', { method: 'POST', body: JSON.stringify(data) }),
  me: () => apiRequest<{ data: AccountProfile }>('/me'),

  // Generic tenant-scoped resource helpers
  list: <T>(resource: string) => apiRequest<{ data: T[] }>(`/${resource}`),
  get: <T>(resource: string, id: number | string) => apiRequest<{ data: T }>(`/${resource}/${id}`),
  create: <T>(resource: string, data: unknown) => apiRequest<{ data: T }>(`/${resource}`, { method: 'POST', body: JSON.stringify(data) }),
  update: <T>(resource: string, id: number | string, data: unknown) => apiRequest<{ data: T }>(`/${resource}/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (resource: string, id: number | string) => apiRequest<{ data: { id: number } }>(`/${resource}/${id}`, { method: 'DELETE' }),

  // Typed convenience wrappers
  students: () => apiRequest<{ data: StudentRow[] }>('/students'),
  student: (id: number | string) => apiRequest<{ data: StudentRow }>(`/students/${id}`),
  createStudent: (data: unknown) => apiRequest<{ data: { id: number } }>('/students', { method: 'POST', body: JSON.stringify(data) }),
  bulkCreateStudents: (rows: unknown[]) => apiRequest<{ data: BulkResult }>('/students/bulk', { method: 'POST', body: JSON.stringify({ rows }) }),
  leads: () => apiRequest<{ data: LeadRow[] }>('/leads'),
  createLead: (data: unknown) => apiRequest<{ data: { id: number } }>('/leads', { method: 'POST', body: JSON.stringify(data) }),
  updateLead: (id: number, data: unknown) => apiRequest<{ data: { id: number } }>(`/leads/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // Users / team / counselors
  users: () => apiRequest<{ data: UserRow[] }>('/users'),
  inviteUser: (data: unknown) => apiRequest<{ data: { id: number; email: string; tempPassword: string } }>('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id: number, data: unknown) => apiRequest<{ data: { id: number } }>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // Appointments
  appointments: () => apiRequest<{ data: AppointmentRow[] }>('/appointments'),
  createAppointment: (data: unknown) => apiRequest<{ data: { id: number } }>('/appointments', { method: 'POST', body: JSON.stringify(data) }),

  // Tasks
  tasks: () => apiRequest<{ data: TaskRow[] }>('/tasks'),
  createTask: (data: unknown) => apiRequest<{ data: { id: number } }>('/tasks', { method: 'POST', body: JSON.stringify(data) }),
  updateTask: (id: number, data: unknown) => apiRequest<{ data: { id: number } }>(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // Leaves
  leaves: () => apiRequest<{ data: LeaveRow[] }>('/leaves'),
  updateLeave: (id: number, data: unknown) => apiRequest<{ data: { id: number } }>(`/leaves/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // Notifications
  notifications: () => apiRequest<{ data: NotificationRow[] }>('/notifications'),
  markNotificationRead: (id: number) => apiRequest<{ data: { id: number } }>(`/notifications/${id}`, { method: 'PATCH' }),
  markAllNotificationsRead: () => apiRequest<{ data: { ok: boolean } }>('/notifications/read-all', { method: 'POST' }),

  // Applications
  applications: () => apiRequest<{ data: ApplicationRow[] }>('/applications'),
  createApplication: (data: unknown) => apiRequest<{ data: { id: number } }>('/applications', { method: 'POST', body: JSON.stringify(data) }),
  updateApplication: (id: number, data: unknown) => apiRequest<{ data: { id: number } }>(`/applications/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  bulkCreateApplications: (rows: unknown[]) => apiRequest<{ data: BulkResult }>('/applications/bulk', { method: 'POST', body: JSON.stringify({ rows }) }),

  // Institutions & courses
  institutions: () => apiRequest<{ data: InstitutionRow[] }>('/institutions'),
  createInstitution: (data: unknown) => apiRequest<{ data: { id: number } }>('/institutions', { method: 'POST', body: JSON.stringify(data) }),
  bulkCreateInstitutions: (rows: unknown[]) => apiRequest<{ data: BulkResult }>('/institutions/bulk', { method: 'POST', body: JSON.stringify({ rows }) }),
  courses: () => apiRequest<{ data: CourseRow[] }>('/courses'),
  createCourse: (data: unknown) => apiRequest<{ data: { id: number } }>('/courses', { method: 'POST', body: JSON.stringify(data) }),
  bulkCreateCourses: (rows: unknown[]) => apiRequest<{ data: BulkResult }>('/courses/bulk', { method: 'POST', body: JSON.stringify({ rows }) }),

  // Documents
  documents: () => apiRequest<{ data: DocumentRow[] }>('/documents'),
  createDocument: (data: unknown) => apiRequest<{ data: { id: number } }>('/documents', { method: 'POST', body: JSON.stringify(data) }),
  updateDocument: (id: number, data: unknown) => apiRequest<{ data: { id: number } }>(`/documents/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  uploadDocuments: (formData: FormData) => apiUpload<{ data: { created: number } }>('/documents/upload', formData),
  openDocumentFile: (id: number) => openAuthedFile(`/documents/${id}/file`),

  // Finance
  invoices: () => apiRequest<{ data: InvoiceRow[] }>('/invoices'),
  createInvoice: (data: unknown) => apiRequest<{ data: { id: number; invoiceNumber: string } }>('/invoices', { method: 'POST', body: JSON.stringify(data) }),
  updateInvoice: (id: number, data: unknown) => apiRequest<{ data: { id: number } }>(`/invoices/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  invoicePayments: (id: number) => apiRequest<{ data: InvoicePaymentRow[] }>(`/invoices/${id}/payments`),
  expenses: () => apiRequest<{ data: ExpenseRow[] }>('/expenses'),
  createExpense: (data: unknown) => apiRequest<{ data: { id: number } }>('/expenses', { method: 'POST', body: JSON.stringify(data) }),
  updateExpense: (id: number, data: unknown) => apiRequest<{ data: { id: number } }>(`/expenses/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // Visa
  visaCases: () => apiRequest<{ data: VisaRow[] }>('/visa-cases'),
  createVisaCase: (data: unknown) => apiRequest<{ data: { id: number } }>('/visa-cases', { method: 'POST', body: JSON.stringify(data) }),
  updateVisaCase: (id: number, data: unknown) => apiRequest<{ data: { id: number } }>(`/visa-cases/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // Account
  consultancy: () => apiRequest<{ data: ConsultancyRow }>('/consultancy'),
  updateConsultancy: (data: unknown) => apiRequest<{ data: { id: number } }>('/consultancy', { method: 'PATCH', body: JSON.stringify(data) }),
  updateMe: (data: unknown) => apiRequest<{ data: { id: number } }>('/me', { method: 'PATCH', body: JSON.stringify(data) }),

  // HR — employees
  employees: () => apiRequest<{ data: EmployeeRow[] }>('/employees'),
  createEmployee: (data: unknown) => apiRequest<{ data: { id: number } }>('/employees', { method: 'POST', body: JSON.stringify(data) }),
  updateEmployee: (id: number, data: unknown) => apiRequest<{ data: { id: number } }>(`/employees/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  enrollFace: (id: number, descriptor: number[]) => apiRequest<{ data: { id: number; enrolled: boolean } }>(`/employees/${id}/face-enroll`, { method: 'POST', body: JSON.stringify({ descriptor }) }),
  clearFace: (id: number) => apiRequest<{ data: { id: number; enrolled: boolean } }>(`/employees/${id}/face-enroll`, { method: 'DELETE' }),

  // HR — attendance
  attendance: () => apiRequest<{ data: AttendanceRow[] }>('/attendance'),
  attendanceToday: () => apiRequest<{ data: AttendanceToday | null }>('/attendance/me/today'),
  checkIn: () => apiRequest<{ data: { ok: boolean } }>('/attendance/check-in', { method: 'POST' }),
  checkOut: () => apiRequest<{ data: { ok: boolean } }>('/attendance/check-out', { method: 'POST' }),
  facePunch: (descriptor: number[]) => apiRequest<{ data: FacePunchResult }>('/attendance/face-punch', { method: 'POST', body: JSON.stringify({ descriptor }) }),

  // HR — leave workflow
  createLeave: (data: unknown) => apiRequest<{ data: { id: number } }>('/leaves', { method: 'POST', body: JSON.stringify(data) }),
  leaveTypes: () => apiRequest<{ data: LeaveTypeRow[] }>('/leave-types'),
  createLeaveType: (data: unknown) => apiRequest<{ data: { id: number } }>('/leave-types', { method: 'POST', body: JSON.stringify(data) }),
  removeLeaveType: (id: number) => apiRequest<{ data: { id: number } }>(`/leave-types/${id}`, { method: 'DELETE' }),
  leaveBalances: () => apiRequest<{ data: LeaveBalances }>('/leaves/balances'),

  // HR — holidays
  holidays: () => apiRequest<{ data: HolidayRow[] }>('/holidays'),
  createHoliday: (data: unknown) => apiRequest<{ data: { id: number } }>('/holidays', { method: 'POST', body: JSON.stringify(data) }),
  removeHoliday: (id: number) => apiRequest<{ data: { id: number } }>(`/holidays/${id}`, { method: 'DELETE' }),

  // Payroll
  payrollRuns: () => apiRequest<{ data: PayrollRunRow[] }>('/payroll'),
  payrollRun: (id: number) => apiRequest<{ data: { run: PayrollRunRow; payslips: PayslipRow[] } }>(`/payroll/${id}`),
  generatePayroll: (data: unknown) => apiRequest<{ data: { id: number; employees: number } }>('/payroll', { method: 'POST', body: JSON.stringify(data) }),
  updatePayrollRun: (id: number, status: string) => apiRequest<{ data: { id: number } }>(`/payroll/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  updatePayslip: (id: number, status: string) => apiRequest<{ data: { id: number } }>(`/payslips/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),

  // Activity
  activity: () => apiRequest<{ data: ActivityRow[] }>('/activity'),
  createActivity: (data: unknown) => apiRequest<{ data: { ok: boolean } }>('/activity', { method: 'POST', body: JSON.stringify(data) }),

  // Analytics
  analyticsOverview: () => apiRequest<{ data: AnalyticsOverview }>('/analytics/overview'),
  leadsTrend: () => apiRequest<{ data: LeadsTrendPoint[] }>('/analytics/leads-trend'),
  studentStatus: () => apiRequest<{ data: StudentStatusSlice[] }>('/analytics/student-status'),
  followUpsToday: () => apiRequest<{ data: FollowUpRow[] }>('/analytics/follow-ups-today'),
  counselorPerformance: () => apiRequest<{ data: CounselorPerfRow[] }>('/analytics/counselor-performance'),
  leadConversion: () => apiRequest<{ data: LeadConversionRow[] }>('/analytics/lead-conversion'),
  appointmentSummary: () => apiRequest<{ data: AppointmentSummaryRow[] }>('/analytics/appointment-summary'),
};

export interface AccountProfile {
  id: number; fullName: string; email: string; role: string; phone?: string | null;
  notificationPrefs?: Record<string, boolean> | null;
  consultancyId: number; consultancyName: string; subscriptionPlan: string;
  subscriptionStatus?: string | null; trialEndsAt?: string | null;
}
